import { useEffect,useMemo,useState } from 'react';
import AdminLayout from '../../components/AdminLayout';
import {
  dispatchBookingNotifications,
  getAdminNotifications,
  markWhatsAppNotificationSent,
  requeueNotification,
  type CustomerNotification,
  type NotificationDispatchResult,
} from '../../lib/notification-api';

const eventNames:Record<string,string>={
  booking_received:'Booking received',
  time_assigned:'Time assigned',
  approved:'Approved',
  payment_requested:'Payment requested',
  confirmed:'Confirmed',
  rescheduled:'Rescheduled',
  declined:'Declined',
  cancelled:'Cancelled',
};

function formatDate(value:string){return new Intl.DateTimeFormat('en-NG',{day:'numeric',month:'short',year:'numeric',hour:'numeric',minute:'2-digit'}).format(new Date(value))}
function whatsappUrl(row:CustomerNotification){const phone=row.customers?.whatsapp_normalized||row.recipient||row.customers?.whatsapp.replace(/\D/g,'').replace(/^0/,'234')||'';return`https://wa.me/${phone}?text=${encodeURIComponent(row.message_text)}`}
function resultMessage(result:NotificationDispatchResult){
  if(result.sent)return`${result.sent} email notification${result.sent===1?'':'s'} sent${result.failed?`; ${result.failed} failed`:''}.`;
  if(result.configuration_required)return result.message||'Email provider configuration is required.';
  if(result.pending_count===0)return'No email notifications are waiting.';
  return result.message||'Notification queue checked.';
}

export default function AdminNotifications(){
  const[rows,setRows]=useState<CustomerNotification[]>([]);
  const[loading,setLoading]=useState(true);
  const[working,setWorking]=useState(false);
  const[message,setMessage]=useState('');
  const[channel,setChannel]=useState('all');
  const[status,setStatus]=useState('all');
  const[query,setQuery]=useState('');
  const[health,setHealth]=useState<NotificationDispatchResult|null>(null);

  async function refresh(){
    const[notifications,provider]=await Promise.all([
      getAdminNotifications(),
      dispatchBookingNotifications({dryRun:true}),
    ]);
    setRows(notifications);
    setHealth(provider);
  }

  useEffect(()=>{refresh().catch(error=>setMessage(error instanceof Error?error.message:'Unable to load notifications.')).finally(()=>setLoading(false))},[]);

  const visible=useMemo(()=>rows.filter(row=>{
    const text=`${row.subject} ${row.message_text} ${row.customers?.full_name||''} ${row.bookings?.reference||''}`.toLowerCase();
    return(channel==='all'||row.channel===channel)&&(status==='all'||row.status===status)&&text.includes(query.toLowerCase());
  }),[rows,channel,status,query]);

  const counts={
    queued:rows.filter(row=>['queued','processing','configuration_required'].includes(row.status)).length,
    sent:rows.filter(row=>['sent','manual_sent'].includes(row.status)).length,
    failed:rows.filter(row=>row.status==='failed').length,
    manual:rows.filter(row=>row.status==='manual_ready').length,
  };

  async function processQueue(){
    setWorking(true);setMessage('Processing email queue…');
    try{const result=await dispatchBookingNotifications({limit:50});setMessage(resultMessage(result));await refresh()}
    catch(error){setMessage(error instanceof Error?error.message:'Unable to process notifications.')}
    finally{setWorking(false)}
  }

  async function retry(row:CustomerNotification){
    setWorking(true);setMessage('Retrying notification…');
    try{
      await requeueNotification(row.id);
      if(row.channel==='email')setMessage(resultMessage(await dispatchBookingNotifications({notificationIds:[row.id]})));
      else setMessage('WhatsApp message is ready to send manually.');
      await refresh();
    }catch(error){setMessage(error instanceof Error?error.message:'Unable to retry notification.')}
    finally{setWorking(false)}
  }

  async function markSent(row:CustomerNotification){
    setWorking(true);setMessage('');
    try{await markWhatsAppNotificationSent(row.id);setMessage('WhatsApp message marked as sent.');await refresh()}
    catch(error){setMessage(error instanceof Error?error.message:'Unable to update WhatsApp notification.')}
    finally{setWorking(false)}
  }

  return <AdminLayout title="Notifications" description="Track every customer email and WhatsApp confirmation from booking request to completion." actions={<button className="studio-admin-btn primary" onClick={processQueue} disabled={working}>{working?'Working…':'Process email queue'}</button>}>
    {message&&<p className={/unable|failed|required|expired/i.test(message)?'studio-admin-error':'studio-admin-feedback'} role="status">{message}</p>}
    <section className="studio-admin-grid metrics">
      <article className="studio-admin-metric"><span>Waiting/configuration</span><strong>{loading?'—':counts.queued}</strong><small>Email outbox</small></article>
      <article className="studio-admin-metric"><span>Manual WhatsApp</span><strong>{loading?'—':counts.manual}</strong><small>Ready to send</small></article>
      <article className="studio-admin-metric"><span>Delivered/marked sent</span><strong>{loading?'—':counts.sent}</strong><small>All channels</small></article>
      <article className="studio-admin-metric"><span>Failed</span><strong>{loading?'—':counts.failed}</strong><small>Available for retry</small></article>
    </section>
    <section className="studio-admin-card studio-admin-grid">
      <div><p className="admin-eyebrow">DELIVERY HEALTH</p><h2>Resend email and manual WhatsApp</h2><p>Email is sent automatically only when the backend API key and a verified sender address are configured. WhatsApp remains manual until a WhatsApp Business provider is connected.</p></div>
      <div style={{display:'grid',gridTemplateColumns:'repeat(3,minmax(0,1fr))',gap:14}}>
        <div><small>EMAIL PROVIDER</small><p><span className={`studio-admin-status ${health?.provider_configured?'confirmed':'awaiting_payment'}`}>{health?.provider_configured?'Configured':'Setup required'}</span></p></div>
        <div><small>AUTOMATIC EMAIL</small><p>{health?.email_enabled?'Enabled':'Disabled'}</p></div>
        <div><small>EMAILS WAITING</small><p>{health?.pending_count??0}</p></div>
      </div>
      {!health?.provider_configured&&<p><strong>Required backend setup:</strong> add <code>RESEND_API_KEY</code> and a verified <code>RESEND_FROM_EMAIL</code> in Supabase Edge Function secrets. No secret is stored in the browser or database.</p>}
    </section>
    <section className="studio-admin-card studio-admin-grid">
      <div style={{display:'grid',gridTemplateColumns:'2fr 1fr 1fr',gap:14}}>
        <label className="studio-admin-field">Search<input value={query} onChange={event=>setQuery(event.target.value)} placeholder="Customer, reference or message"/></label>
        <label className="studio-admin-field">Channel<select value={channel} onChange={event=>setChannel(event.target.value)}><option value="all">All channels</option><option value="email">Email</option><option value="whatsapp">WhatsApp</option></select></label>
        <label className="studio-admin-field">Status<select value={status} onChange={event=>setStatus(event.target.value)}><option value="all">All statuses</option><option value="queued">Queued</option><option value="configuration_required">Setup required</option><option value="manual_ready">Manual ready</option><option value="sent">Sent</option><option value="manual_sent">Manual sent</option><option value="failed">Failed</option><option value="skipped">Skipped</option><option value="superseded">Superseded</option></select></label>
      </div>
      <div className="studio-admin-table">
        {loading?<p>Loading notifications…</p>:visible.length?visible.map(row=><article className="studio-admin-row" key={row.id}>
          <div style={{display:'grid',gridTemplateColumns:'1.2fr 1fr auto',gap:16,alignItems:'start'}}>
            <div><strong>{row.customers?.full_name||'Customer'} · {eventNames[row.event_type]||row.event_type}</strong><small style={{display:'block'}}>{row.bookings?.reference||'Booking'} · {formatDate(row.created_at)}</small><p style={{whiteSpace:'pre-wrap',marginBottom:0}}>{row.message_text}</p></div>
            <div><strong>{row.channel==='email'?'Email':'WhatsApp'}</strong><small style={{display:'block',overflowWrap:'anywhere'}}>{row.recipient||'No recipient'}</small><p><span className={`studio-admin-status ${row.status}`}>{row.status.replaceAll('_',' ')}</span></p>{row.last_error&&<small style={{display:'block'}}>{row.last_error}</small>}</div>
            <div className="studio-admin-actions" style={{justifyContent:'flex-end'}}>
              {row.channel==='whatsapp'&&row.status==='manual_ready'&&<><a className="studio-admin-btn primary" href={whatsappUrl(row)} target="_blank" rel="noreferrer">Open WhatsApp</a><button className="studio-admin-btn" onClick={()=>markSent(row)} disabled={working}>Mark sent</button></>}
              {['failed','configuration_required','skipped'].includes(row.status)&&<button className="studio-admin-btn" onClick={()=>retry(row)} disabled={working}>Retry</button>}
            </div>
          </div>
        </article>):<p>No matching notifications.</p>}
      </div>
    </section>
  </AdminLayout>
}
